import {Alert, AlertTitle, Box, Grid} from "@mui/material";
import {BreadcrumbItem, BreadcrumbLink, Breadcrumbs, ConfirmButton} from "@variocube/app-ui";
import React, {useEffect, useState} from "react";
import {useAsync} from "react-async-hook";
import {Link, useNavigate} from "react-router-dom";
import {Compartment, CompartmentMutation, LocationAncestry, LocationTree, useCompartmentApi} from "../../api";
import {useLocalization, useWordingFlavor} from "@smalox/shared";
import {useLocationPermission, useTenantId} from "../../tenant";
import {useNumericParam} from "../../utils/useParam";
import {View, ViewHeader} from "../View";
import {CompartmentEditForm} from "./CompartmentEditForm";
import {CompartmentLocationForm} from "./CompartmentLocationForm";
import {CompartmentLockForm} from "./CompartmentLockForm";

export function CompartmentEdit() {
	const {t} = useLocalization();
	const wordingFlavor = useWordingFlavor();
	const navigate = useNavigate();
	const compartmentId = useNumericParam("compartmentId");
	const tenantId = useTenantId();

	const [compartment, setCompartment] = useState<Compartment>();

	const {getCompartment, updateCompartment, assignCompartmentLock, assignCompartmentLocation, deleteCompartment} =
		useCompartmentApi();
	const {loading, error, result} = useAsync(getCompartment, [compartmentId]);

	const canDelete = useLocationPermission("COMPARTMENT_DELETE", result?.location.id);

	useEffect(() => {
		setCompartment(result);
	}, [result]);

	async function handleSave(mutation: CompartmentMutation) {
		setCompartment(await updateCompartment(compartmentId, mutation));
	}

	async function assignLock(lockId: string | undefined) {
		setCompartment(await assignCompartmentLock(compartmentId, lockId));
	}

	async function assignLocation(location: LocationAncestry | LocationTree) {
		setCompartment(await assignCompartmentLocation(compartmentId, location.id));
	}

	async function handleDelete() {
		await deleteCompartment(compartmentId);
		navigate(`/${tenantId}/compartments`);
	}

	return (
		<View error={error}>
			<Breadcrumbs>
				<BreadcrumbLink component={Link} to={"../.."} relative="path">{t(`compartments.title.${wordingFlavor}`)}</BreadcrumbLink>
				<BreadcrumbLink component={Link} to={".."} relative="path">{compartment?.number}</BreadcrumbLink>
				<BreadcrumbItem>{t("edit")}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader title={t(`compartments.edit.title.${wordingFlavor}`, compartment)} />
			<Box>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<CompartmentEditForm loading={loading} compartment={compartment} onSave={handleSave} />
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<CompartmentLockForm loading={loading} lockId={compartment?.lock?.id} onSave={assignLock} />
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<CompartmentLocationForm
							loading={loading}
							location={compartment?.location}
							onSave={assignLocation}
						/>
					</Grid>
				</Grid>
			</Box>
			{canDelete && (
				<Alert
					severity="error"
					action={
						<ConfirmButton
							cancel={t("cancel")}
							title={t(`compartments.delete.title.${wordingFlavor}`)}
							onConfirm={handleDelete}
							color="error"
						>
							{t(`compartments.delete.confirm.${wordingFlavor}`)}
						</ConfirmButton>
					}
				>
					<AlertTitle>
						{t(`compartments.delete.title.${wordingFlavor}`)}
					</AlertTitle>
					{t(`compartments.delete.message.${wordingFlavor}`)}
				</Alert>
			)}
		</View>
	);
}
